import clsx from 'clsx';
import { Check, ChevronDown, Copy, FileText, Loader2 } from 'lucide-react';
import { useState, type ComponentProps, type MouseEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
// highlight.js theme — github-light to match the rest of the panel.
// Imported here (the only place that uses syntax highlighting) so it
// only ships in bundles that actually open the chat.
import 'highlight.js/styles/github.css';
import { PhiaMark } from '../../widget-shell/PhiaMark';
import type { Message, ThreadStatus, ThreadStatusState } from './useThread';
import styles from './ThreadsView.module.css';

// Etiqueta visible para cada estado intermedio del asistente.
const STATUS_LABELS: Record<ThreadStatusState, string> = {
  thinking: 'Pensando…',
  selecting: 'Seleccionando módulos…',
  reading: 'Leyendo contenido…'
};

// Copy-to-clipboard with a 2s confirmation flash. Used by the per-message
// action and by the per-codeblock button. Kept tiny on purpose — we don't
// need the whole `use-copy-to-clipboard` lib.
function useCopy(timeout = 2000) {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), timeout);
    } catch {
      // Clipboard API blocked (iframe permissions, http page) — ignore
      // silently; there's no useful recovery and the chat must keep going.
    }
  };
  return { copied, copy };
}

export function MessageBubble({ msg }: { msg: Message }) {
  const isAssistant = msg.role === 'assistant';
  return (
    <div className={clsx(styles.row, isAssistant ? styles.fromBot : styles.fromUser)}>
      {isAssistant && (
        <div className={styles.avatar} aria-hidden>
          <PhiaMark className={styles.avatarMark} />
        </div>
      )}
      <div className={styles.bubbleGroup}>
        <div className={clsx(styles.bubble, isAssistant ? styles.bot : styles.user)}>
          {renderContent(msg, isAssistant)}
          {msg.pending && msg.content && <span className={styles.caret} />}
        </div>
        {isAssistant && msg.content && !msg.pending && (
          <MessageActions text={msg.content} />
        )}
      </div>
    </div>
  );
}

function renderContent(msg: Message, isAssistant: boolean) {
  // La traza de actividad (fases + módulos consultados) sólo aplica al
  // asistente. En vivo mientras no hay texto; colapsada encima de la
  // respuesta una vez empieza a llegar.
  const steps = isAssistant ? msg.steps ?? [] : [];
  const trace =
    steps.length > 0 ? (
      <ActivityTrace steps={steps} live={!!msg.pending && !msg.content} />
    ) : null;

  if (!msg.content) {
    if (!msg.pending) return trace;
    // Mientras no llega texto: la traza en vivo si ya hay fases, o los
    // puntos suspensivos como reserva.
    return trace ?? <TypingDots />;
  }

  // User messages stay as plain text — short, no formatting needed.
  if (!isAssistant) return msg.content;
  // Assistant: traza colapsada (si hubo) encima del markdown + código.
  return (
    <>
      {trace}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { ignoreMissing: true }]]}
        components={{ pre: CodeBlock }}
      >
        {msg.content}
      </ReactMarkdown>
    </>
  );
}

// Wraps every <pre> from ReactMarkdown with a copy button that grabs
// the raw text of the code block. Matches how Study renders code blocks.
function CodeBlock({ children, ...rest }: ComponentProps<'pre'>) {
  const { copied, copy } = useCopy();
  const onCopy = (e: MouseEvent<HTMLButtonElement>) => {
    const pre = e.currentTarget.parentElement;
    const text = pre?.querySelector('code')?.textContent ?? pre?.textContent ?? '';
    void copy(text);
  };
  return (
    <pre {...rest} className={styles.codeBlock}>
      <button
        type="button"
        onClick={onCopy}
        className={styles.codeCopyBtn}
        aria-label={copied ? 'Copiado' : 'Copiar código'}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      {children}
    </pre>
  );
}

// Subtle action row under assistant bubbles — currently just "copy
// message". Hidden until hover/focus on the bubble group so it doesn't
// distract from the conversation when the user isn't reaching for it.
function MessageActions({ text }: { text: string }) {
  const { copied, copy } = useCopy();
  return (
    <div className={styles.messageActions}>
      <button
        type="button"
        onClick={() => copy(text)}
        className={styles.messageActionBtn}
        aria-label={copied ? 'Copiado' : 'Copiar respuesta'}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        <span>{copied ? 'Copiado' : 'Copiar'}</span>
      </button>
    </div>
  );
}

function TypingDots() {
  return (
    <span className={styles.typing} aria-label="Escribiendo">
      <span /><span /><span />
    </span>
  );
}

// Módulos únicos que el asistente consultó a lo largo de la traza, en
// orden de aparición. Es lo que resumimos en la vista colapsada.
function uniqueModules(steps: ThreadStatus[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of steps) {
    for (const name of s.modules ?? []) {
      if (!seen.has(name)) {
        seen.add(name);
        out.push(name);
      }
    }
  }
  return out;
}

// Chips de módulos reutilizados por la vista en vivo y la colapsada.
function ModuleChips({ modules }: { modules: string[] }) {
  return (
    <span className={styles.statusModules}>
      {modules.map((name, i) => (
        <span key={`${name}-${i}`} className={styles.statusModule}>
          <FileText size={12} className={styles.statusModuleIcon} aria-hidden />
          <span className={styles.statusModuleName}>{name}</span>
        </span>
      ))}
    </span>
  );
}

// Traza de actividad del asistente.
//  • live: las fases (thinking → selecting → reading) se acumulan; la
//    última lleva spinner y las previas un check, con sus módulos como
//    chips. Es lo que se ve mientras todavía no llega texto.
//  • colapsada: terminada la fase de búsqueda, se resume en una línea
//    clicable ("Consulté N módulos") que despliega los módulos, y queda
//    encima de la respuesta como registro de lo que Phia leyó.
function ActivityTrace({ steps, live }: { steps: ThreadStatus[]; live: boolean }) {
  const [open, setOpen] = useState(false);

  if (live) {
    return (
      <div className={styles.trace} aria-live="polite">
        {steps.map((s, i) => {
          const current = i === steps.length - 1;
          return (
            <div key={`${s.state}-${i}`} className={styles.traceStep}>
              <span className={styles.traceIcon}>
                {current ? (
                  <Loader2 size={14} className={styles.statusSpinner} aria-hidden />
                ) : (
                  <Check size={14} className={styles.traceCheck} aria-hidden />
                )}
              </span>
              <span
                className={clsx(styles.traceLabel, current && styles.traceLabelActive)}
              >
                {STATUS_LABELS[s.state]}
              </span>
              {s.modules && s.modules.length > 0 && (
                <ModuleChips modules={s.modules} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Vista colapsada: sólo tiene sentido si llegó a consultar módulos.
  const modules = uniqueModules(steps);
  if (modules.length === 0) return null;
  return (
    <div className={styles.traceDone}>
      <button
        type="button"
        className={styles.traceToggle}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Check size={13} className={styles.traceCheck} aria-hidden />
        <span>
          Consulté {modules.length}{' '}
          {modules.length === 1 ? 'módulo' : 'módulos'}
        </span>
        <ChevronDown
          size={13}
          className={clsx(styles.traceChevron, open && styles.traceChevronOpen)}
          aria-hidden
        />
      </button>
      {open && (
        <div className={styles.traceModulesList}>
          <ModuleChips modules={modules} />
        </div>
      )}
    </div>
  );
}
